const STORAGE_PREFIX = 'cc_';
const VISITOR_ID_KEY = `${STORAGE_PREFIX}visitor_id`;
const CONVERSATION_PREFIX = `${STORAGE_PREFIX}conversation_`;
const SESSION_PREFIX = `${STORAGE_PREFIX}session_`;

/**
 * Generate a unique visitor ID
 */
function generateVisitorId(): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomPart}`;
}

/**
 * Check if localStorage is available
 */
function isStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// In-memory fallback storage
const memoryStorage: Map<string, string> = new Map();

/**
 * Get item from storage with fallback to memory
 */
function getItem(key: string): string | null {
  if (isStorageAvailable()) {
    return localStorage.getItem(key);
  }
  return memoryStorage.get(key) ?? null;
}

/**
 * Set item in storage with fallback to memory
 */
function setItem(key: string, value: string): void {
  if (isStorageAvailable()) {
    localStorage.setItem(key, value);
  } else {
    memoryStorage.set(key, value);
  }
}

/**
 * Remove item from storage with fallback to memory
 */
function removeItem(key: string): void {
  if (isStorageAvailable()) {
    localStorage.removeItem(key);
  } else {
    memoryStorage.delete(key);
  }
}

/**
 * Get the visitor ID from storage or generate a new one
 */
export function getVisitorId(): string {
  let visitorId = getItem(VISITOR_ID_KEY);

  if (!visitorId) {
    visitorId = generateVisitorId();
    setItem(VISITOR_ID_KEY, visitorId);
  }

  return visitorId;
}

/**
 * Set the visitor ID in storage
 */
export function setVisitorId(id: string): void {
  setItem(VISITOR_ID_KEY, id);
}

/**
 * Check if a stored value is valid (not "undefined", "null", or empty)
 */
function isValidStoredValue(value: string | null): value is string {
  return value !== null && value !== '' && value !== 'undefined' && value !== 'null';
}

/**
 * Get the conversation ID for a specific tenant.
 * Always returns null — conversation state lives only in React state
 * and resets on page reload.
 */
export function getConversationId(_tenantSlug: string): string | null {
  return null;
}

/**
 * Set the conversation ID for a specific tenant.
 * No-op — conversation state is not persisted across page reloads.
 */
export function setConversationId(_tenantSlug: string, _id: string): void {
  // no-op
}

/**
 * Get the session ID for a specific tenant.
 * Always returns null — session resets on page reload.
 */
export function getSessionId(_tenantSlug: string): string | null {
  return null;
}

/**
 * Set the session ID for a specific tenant.
 * No-op — session state is not persisted across page reloads.
 */
export function setSessionId(_tenantSlug: string, _id: string): void {
  // no-op
}

/**
 * Clear conversation data for a specific tenant
 */
export function clearConversation(tenantSlug: string): void {
  removeItem(`${CONVERSATION_PREFIX}${tenantSlug}`);
  removeItem(`${SESSION_PREFIX}${tenantSlug}`);
}

/**
 * Clear all Chat Checkout data from storage
 */
export function clearAllData(): void {
  if (isStorageAvailable()) {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } else {
    memoryStorage.clear();
  }
}

// Export storage service as an object for convenience
export const storageService = {
  getVisitorId,
  setVisitorId,
  getConversationId,
  setConversationId,
  getSessionId,
  setSessionId,
  clearConversation,
  clearAllData,
};
